import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { initKeycloak, initKeycloakAuth, logoutToLogoutPage, redirectToLogin } from '../services/keycloakService';
import { useAuthStore } from '../store/authStore';
import { useUserStore } from '../store/userStore';
import { sendKeycloakTokens, clearAuthCookies } from '@shared/services/authApi';
import { getMyProfile } from '../../profile/services/profileService';
import { activateDefaultSite, getDefaultSite } from '../utils/defaultSite';
import {
  getUserCompanies,
  selectCompany,
  getRoles,
  getUserRoles,
  verifyPasswordSetup,
} from '../services/authService';
import { TOKEN_KEY, REFRESH_TOKEN_KEY } from '@shared/constants/config';
import { getSubdomain, resolveTenant, isDomainChanged, buildMainDomainUrl, buildTenantUrl } from '@shared/lib/tenant';
import { getLastActivity } from '@shared/lib/idleActivity';
import { isLoggedOutMarked, clearLoggedOutMark } from '@shared/lib/loggedOutMarker';

const isNetworkError = (error) => {
  return (
    error?.message === 'Network Error' ||
    error?.code === 'ERR_NETWORK' ||
    error?.code === 'ERR_CONNECTION_REFUSED' ||
    !error?.response
  );
};

// keycloak-js clears its tokens (authenticated=false, refresh token removed) when Keycloak refuses a
// refresh — the SSO session is over. A refresh that failed for any other reason (offline after a
// laptop wakes, Keycloak briefly unreachable) leaves them in place and can be retried.
const isSessionEnded = (keycloakInstance) =>
  !keycloakInstance?.authenticated || !keycloakInstance?.refreshToken;

// While the user is active the token is refreshed at least this often, so Keycloak's SSO Session Idle
// clock (which only sees refreshes) stays within about a minute of the user's real activity. That is
// what lets Keycloak's idle limit sit just above the frontend's idle timeout.
const ACTIVE_REFRESH_INTERVAL_MS = 60 * 1000;

/**
 * Owns the Keycloak/tenant bootstrap: pre-init tenant resolution + IdP pinning, token sync, the
 * new-login vs active-session hydration branch, firm auto-select / subdomain hand-off, proactive
 * token refresh, and logout. Extracted from `KeycloakProvider` so that component is a thin shell.
 *
 * There is no manual firm picker at login: `selectInitialCompany` always activates the first firm
 * `GET /api/firm` returns (or the tenant-subdomain-matched one, if on a company subdomain). A
 * multi-firm user can switch afterward via the sidebar `FirmSelector`/`useCompanySwitcher`, which is
 * fully independent of this bootstrap.
 *
 * @returns bootstrap state consumed by `KeycloakProvider`.
 */
export function useTenantBootstrap() {
  const [keycloak, setKeycloak] = useState(null);
  const [initialized, setInitialized] = useState(false);
  // Subdomain SSO: IdP alias to pin at login (null on the main domain), and whether the current
  // subdomain differs from the one the user last visited (drives the domain-change warning banner).
  const [idpHint, setIdpHint] = useState(null);
  const [domainWarning, setDomainWarning] = useState(false);
  const { setAuth, setToken, setSelectedCompany, setSelectedSite, logout: storeLogout } = useAuthStore();
  const { setProfile, clearProfile, setRolesAndPermissions, setAvailableRoles } = useUserStore();
  // The tenant IdP alias, readable from the session handlers below. They are registered inside the
  // bootstrap effect, whose closure only ever sees the first render's `idpHint` state (null).
  const idpHintRef = useRef(null);
  // An expired session can trigger more than one forced logout at once (Keycloak's onAuthLogout and
  // the failed refresh that caused it); the cleanup and redirect run once.
  const logoutStartedRef = useRef(false);
  // When the token was last refreshed — compared with the user's last activity to decide whether an
  // active user's session needs extending (see the refresh interval below).
  const lastRefreshAtRef = useRef(0);

  const clearBackendCookies = async () => {
    try {
      await clearAuthCookies();
    } catch (e) {
      console.warn('[Logout] Failed to clear backend cookies:', e);
    }
  };

  const clearLocalSession = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('auth_user');
    localStorage.removeItem('selected_company');
    localStorage.removeItem('selected_site');
    sessionStorage.removeItem('keycloak_session_active');

    clearProfile();
    storeLogout();
  };

  const logoutUser = async (keycloakInstance) => {
    if (logoutStartedRef.current) return;
    logoutStartedRef.current = true;

    await clearBackendCookies();
    clearLocalSession();

    // Land on the public /logout page so a forced logout (backend unreachable, idle timeout) shows
    // the same "signed out" confirmation + Re Login button as the deliberate logout. Shared with
    // useAuth().logout — see logoutToLogoutPage for the main-host reasoning.
    await logoutToLogoutPage(keycloakInstance);
  };

  // The Keycloak session ended mid-use (idle timeout, laptop asleep, ended by an admin): clean up and
  // send the user straight to sign-in, returning them to the page they were on.
  const expireSession = async (keycloakInstance) => {
    if (logoutStartedRef.current) return;
    logoutStartedRef.current = true;

    clearLocalSession();
    // Start the sign-in redirect BEFORE clearing the cookies: with an expired token that DELETE gets
    // a 401, and the HTTP client's 401 handler would otherwise win the redirect and land the user on
    // /logout. redirectToLogin claims the shared redirect guard synchronously.
    const redirect = redirectToLogin(keycloakInstance, { idpHint: idpHintRef.current });
    await clearBackendCookies();
    await redirect;
  };

  const handleNetworkErrorLogout = async (keycloakInstance) => {
    console.error('[Network Error] Backend unavailable - logging out and clearing session');
    await logoutUser(keycloakInstance);
  };

  const syncTokensWithBackend = async (keycloakInstance) => {
    try {
      await sendKeycloakTokens(
        keycloakInstance.token,
        keycloakInstance.refreshToken,
        keycloakInstance.tokenParsed?.exp,
        keycloakInstance.refreshTokenParsed?.exp
      );
      return true;
    } catch {
      return false;
    }
  };

  // Set the active firm on the backend session, then hydrate profile + roles for it.
  // Shared by the single-company, tenant-subdomain, and manual-picker selection paths.
  const activateCompany = async (company) => {
    await selectCompany(company.firmId);
    // Like the firm itself, the site is not asked for at login: the customer's first site (as the API
    // lists it) is activated automatically and the user can switch from the sidebar afterwards. Point
    // the site cookie at it BEFORE the profile/roles below load (they are site-scoped) — it may also
    // be left over from a previous session/customer. Not fatal — the app is still usable without a
    // site.
    try {
      await activateDefaultSite(company);
      setSelectedSite(getDefaultSite(company.sites));
    } catch (error) {
      console.warn('[Site Selection] Failed to activate the default site after login:', error);
    }
    const profileResponse = await getMyProfile();
    setProfile(profileResponse?.data ?? profileResponse);
    setSelectedCompany(company);
    const rolesResponse = await getRoles();
    setRolesAndPermissions(rolesResponse.roles || [], rolesResponse.permissions || []);
    const userRolesResponse = await getUserRoles();
    setAvailableRoles(userRolesResponse?.data || []);
  };

  // Multi-tenant SSO: when a firm has its own subdomain and we're on the MAIN domain, resolve its
  // tenant (the backend Set-Cookie updates tenant_sub + idp_alias) and hand the session off to that
  // company's subdomain portal, where the re-init auto-selects this firm. Returns true when a
  // redirect was started (the caller must stop and NOT hydrate in place); false to continue here.
  // A firm without a subdomain, or a request already on a subdomain, returns false (no-op).
  const redirectToCompanySubdomain = async (company) => {
    if (!company.subDomain || getSubdomain()) return false;
    // Validate + build the tenant URL BEFORE resolving/redirecting. A malformed subDomain (e.g.
    // one containing `/` or `#`) could break out of the intended host — buildTenantUrl returns null
    // for anything that isn't a safe host label, and we then stay put (the caller activates the firm
    // in place instead of navigating somewhere unexpected).
    const target = buildTenantUrl(company.subDomain, '/');
    if (!target) {
      console.warn('[Company Selection] Refusing redirect to malformed subdomain:', company.subDomain);
      return false;
    }
    try {
      await resolveTenant(company.subDomain);
    } catch (error) {
      console.warn('[Company Selection] Tenant resolve failed before subdomain redirect:', error);
    }
    globalThis.location.href = target;
    return true;
  };

  // Subdomain SSO: resolve the tenant BEFORE Keycloak init so login can pin the IdP. Returns
  // `redirected: true` when it sent the browser to the org picker (an unknown/inactive subdomain),
  // in which case the caller must stop. `resolvedTenant` is the active tenant this subdomain maps
  // to (used post-login to auto-select that firm), or null on a resolver failure.
  const resolveSubdomainTenant = async (subdomain) => {
    // Read the previous-visit cookie for the domain-change check BEFORE resolveTenant overwrites it.
    setDomainWarning(isDomainChanged(subdomain));
    try {
      const tenant = await resolveTenant(subdomain);
      if (!tenant?.isActive) {
        // Unknown/inactive subdomain — send the user to the main-domain org picker.
        globalThis.location.href = buildMainDomainUrl('/select-org');
        return { redirected: true, resolvedTenant: null };
      }
      idpHintRef.current = tenant.idpAlias || null;
      setIdpHint(idpHintRef.current);
      return { redirected: false, resolvedTenant: tenant };
    } catch (error) {
      // A resolver failure must not block login — fall back to the generic (no idpHint) flow.
      console.warn('[Tenant] Failed to resolve tenant for subdomain, continuing without idpHint:', error);
      return { redirected: false, resolvedTenant: null };
    }
  };

  // Push the freshly-minted Keycloak tokens to the backend on a new login. Returns true when a
  // network error forced a logout (the caller must stop); false otherwise (including non-network
  // sync failures, which are logged but non-fatal — the original behaviour).
  const syncNewLoginTokens = async (keycloakInstance) => {
    try {
      await sendKeycloakTokens(keycloakInstance.token, keycloakInstance.refreshToken, keycloakInstance.tokenParsed?.exp, keycloakInstance.refreshTokenParsed?.exp);
      return false;
    } catch (error) {
      console.error('Failed to sync tokens with backend', error);
      if (isNetworkError(error)) {
        await handleNetworkErrorLogout(keycloakInstance);
        return true;
      }
      return false;
    }
  };

  // First-login password-setup verification — best effort, never blocks the flow.
  const verifyPasswordSetupSafely = async () => {
    try {
      const verifyResult = await verifyPasswordSetup();
      if (verifyResult.success) {
        console.log('[Password Setup] User verified and activated');
      } else {
        console.warn('[Password Setup] Failed to verify password setup:', verifyResult.message);
      }
    } catch (error) {
      console.warn('[Password Setup] Error during password setup verification:', error);
    }
  };

  // Pick and activate the firm right after a new login. Returns true when a subdomain hand-off was
  // started (the caller must stop and NOT hydrate in place); false otherwise.
  const selectInitialCompany = async (companiesList, resolvedTenant, subdomain) => {
    // On a tenant subdomain the URL already identifies the company — auto-select the firm this
    // subdomain maps to (by companyId, or subdomain fallback), taking priority over the default
    // first-firm pick below.
    const tenantCompany = resolvedTenant
      ? companiesList.find(
          (c) =>
            c.firmId === resolvedTenant.companyId ||
            (c.subDomain && subdomain && c.subDomain.toLowerCase() === subdomain.toLowerCase())
        )
      : null;

    if (tenantCompany) {
      await activateCompany(tenantCompany);
      return false;
    }
    if (companiesList.length === 0) {
      console.warn('[Company Selection] No companies found for user');
      return false;
    }
    // No manual picker: auto-activate the first firm the API returns. If it has its own subdomain,
    // hand off to that subdomain portal (same as the old manual-picker selection did); otherwise
    // activate it in place and stay on the main domain. Applies regardless of how many firms the
    // user belongs to — a multi-firm user can still switch afterward via the sidebar FirmSelector.
    const defaultCompany = companiesList[0];
    await selectCompany(defaultCompany.firmId);
    if (await redirectToCompanySubdomain(defaultCompany)) return true;
    await activateCompany(defaultCompany);
    return false;
  };

  // New-login branch: mark the session, verify password setup, then select the company. Returns
  // true when a subdomain hand-off was started (the caller must stop).
  const handleNewLogin = async (resolvedTenant, subdomain) => {
    sessionStorage.setItem('keycloak_session_active', 'true');
    await verifyPasswordSetupSafely();
    try {
      const response = await getUserCompanies();
      const companiesList = response || [];
      return await selectInitialCompany(companiesList, resolvedTenant, subdomain);
    } catch (error) {
      console.error('[Company Selection] Failed to fetch companies:', error);
      return false;
    }
  };

  // Existing session (refresh): the firm is already chosen via its cookie. Reads the server's view
  // (`isCurrent`) so the stores match it, and repairs a missing site BEFORE the profile/roles below load
  // (they are site-scoped): a current site is kept; with sites but none current (a session from before
  // multi-site, or an expired site cookie) the customer's first site is saved, the same default a new
  // login gets. Best-effort: a failure is logged and never blocks hydration.
  const ensureActiveSite = async () => {
    try {
      const companiesList = (await getUserCompanies()) || [];
      const active = companiesList.find((c) => c.isCurrent);
      if (!active) return;

      setSelectedCompany(active);
      const sites = Array.isArray(active.sites) ? active.sites : [];
      const current = sites.find((s) => s.isCurrent);
      if (current) {
        setSelectedSite(current);
        return;
      }
      if (sites.length === 0) {
        // Nothing to activate (and nothing to clear on every refresh).
        setSelectedSite(null);
        return;
      }
      await activateDefaultSite(active);
      setSelectedSite(getDefaultSite(sites));
    } catch (error) {
      console.warn('[Site Selection] Failed to ensure an active site for the existing session:', error);
    }
  };

  // Active-session branch: re-hydrate profile + roles without re-selecting the company. Returns true
  // when a network error forced a logout (the caller must stop).
  const hydrateActiveSession = async (keycloakInstance) => {
    console.log('[Company Selection] Session active, skipping company selection');
    await ensureActiveSite();
    try {
      const profileResponse = await getMyProfile();
      setProfile(profileResponse?.data ?? profileResponse);
      const rolesResponse = await getRoles();
      setRolesAndPermissions(rolesResponse.roles || [], rolesResponse.permissions || []);
      const userRolesResponse = await getUserRoles();
      setAvailableRoles(userRolesResponse?.data || []);
      return false;
    } catch (error) {
      console.error('[Identity] Failed to fetch /me/profile on active session:', error);
      if (isNetworkError(error)) {
        await handleNetworkErrorLogout(keycloakInstance);
        return true;
      }
      return false;
    }
  };

  // Refresh the token and re-sync it with the backend. `minValidity` -1 forces a refresh; otherwise
  // Keycloak is only contacted when the token expires within that many seconds.
  const runProactiveRefresh = async (keycloakInstance, minValidity) => {
    try {
      const refreshed = await keycloakInstance.updateToken(minValidity);
      if (refreshed) {
        lastRefreshAtRef.current = Date.now();
        try {
          await syncTokensWithBackend(keycloakInstance);
          setToken(keycloakInstance.token);
        } catch (syncError) {
          if (isNetworkError(syncError)) {
            await handleNetworkErrorLogout(keycloakInstance);
            return;
          }
          console.error('[Token Refresh] Failed to sync tokens:', syncError);
        }
      } else {
        console.log('[Token Refresh] Token not refreshed');
      }
    } catch (error) {
      console.error('[Token Refresh] Proactive token refresh failed:', error);
      // Session over (Keycloak refused the refresh) → back to sign-in instead of leaving the app
      // rendered unauthenticated. Anything else (offline, Keycloak briefly down) is retried on the next tick.
      if (isSessionEnded(keycloakInstance)) {
        await expireSession(keycloakInstance);
      }
    }
  };

  // Wire the session handlers: the on-expiry refresh (re-sync the token, force a logout if the
  // backend is unreachable) and the session-ended logout.
  const registerTokenExpiryHandler = (keycloakInstance) => {
    // The tokens were just issued or synced: the session starts fresh here.
    lastRefreshAtRef.current = Date.now();

    // keycloak-js calls this when it clears its tokens because Keycloak refused a refresh — from the
    // interval below, onTokenExpired, or the HTTP client's 401 retry. Without it the app kept
    // rendering with authenticated=false and ProtectedRoute showed an empty screen; now the user is
    // sent to the sign-in page.
    keycloakInstance.onAuthLogout = () => {
      console.warn('[Session] Keycloak session ended - redirecting to sign-in');
      expireSession(keycloakInstance);
    };

    // Same refresh as the interval below; this fires when the token actually expires (e.g. right
    // after a laptop wakes, before the next interval tick).
    keycloakInstance.onTokenExpired = () => runProactiveRefresh(keycloakInstance, 60);
  };

  // Authenticated branch: sync tokens, run the new-login vs active-session hydration, then arm the
  // refresh handler. Returns true when the caller should stop (a forced logout or subdomain hand-off).
  const hydrateAuthenticatedSession = async (keycloakInstance, resolvedTenant, subdomain) => {
    // A real sign-in completed: the portal is no longer logged out.
    clearLoggedOutMark();
    if (await syncNewLoginTokens(keycloakInstance)) return true;
    setAuth(keycloakInstance.tokenParsed, keycloakInstance.token);

    const isNewLogin = !sessionStorage.getItem('keycloak_session_active');
    if (isNewLogin) {
      if (await handleNewLogin(resolvedTenant, subdomain)) return true;
    } else if (await hydrateActiveSession(keycloakInstance)) {
      return true;
    }

    registerTokenExpiryHandler(keycloakInstance);
    return false;
  };

  // The bootstrap must run exactly once. React StrictMode (dev) runs effects twice, and only the
  // Keycloak init is memoized — the rest would run twice CONCURRENTLY: the first run flags the session
  // as active at its start, so the second takes the existing-session path before the first has chosen
  // the customer/site and loads the app unscoped (the dashboard rendering before the login has finished
  // setting up the firm and site). A ref survives StrictMode's simulated remount, so the second
  // invocation is a no-op while the first run's state updates still land on the mounted component.
  const bootstrapStartedRef = useRef(false);

  useEffect(() => {
    if (bootstrapStartedRef.current) return;
    bootstrapStartedRef.current = true;

    const initializeKeycloak = async () => {
      try {
        const subdomain = getSubdomain();
        // The tenant this subdomain maps to (when on a company subdomain) — used post-login to
        // auto-select that firm instead of prompting with the picker.
        let resolvedTenant = null;
        if (subdomain) {
          const tenantResult = await resolveSubdomainTenant(subdomain);
          if (tenantResult.redirected) return;
          resolvedTenant = tenantResult.resolvedTenant;
        }

        const keycloakInstance = initKeycloak();

        // Onboarding seed: when the previous page (UserOnboardPage) completed a backend-initiated
        // password grant it never sets a Keycloak SSO cookie, so `check-sso` would miss the session.
        // Instead the onboarding flow stashes the Keycloak token triplet in localStorage; hand them
        // to `keycloak-js` init so it starts already authenticated. Consume them exactly once —
        // leaving them around would keep re-seeding the next refresh too.
        const bootToken = localStorage.getItem('kc_boot_token');
        const bootRefreshToken = localStorage.getItem('kc_boot_refresh_token');
        const bootIdToken = localStorage.getItem('kc_boot_id_token');
        const hasBootTokens = bootToken && bootRefreshToken && bootIdToken;
        if (hasBootTokens) {
          localStorage.removeItem('kc_boot_token');
          localStorage.removeItem('kc_boot_refresh_token');
          localStorage.removeItem('kc_boot_id_token');
        }

        // After a portal logout the Keycloak session may still be alive for NumberCruncher Web, and
        // `check-sso` would silently sign the user back in (landing them on the dashboard from the
        // /logout page). Skip it while the logged-out marker is set; keycloak-js still completes a
        // sign-in redirect returning to this page, which clears the marker.
        const silentSso = isLoggedOutMarked() ? {} : { onLoad: 'check-sso' };

        // initKeycloakAuth, not keycloakInstance.init: the promise is memoized so a double mount
        // (StrictMode in dev) awaits the same init rather than throwing on the second call.
        const authenticated = await initKeycloakAuth(hasBootTokens
          ? {
              token: bootToken,
              refreshToken: bootRefreshToken,
              idToken: bootIdToken,
              checkLoginIframe: false,
              pkceMethod: 'S256',
            }
          : {
              ...silentSso,
              checkLoginIframe: false,
              pkceMethod: 'S256',
            });

        if (authenticated) {
          if (await hydrateAuthenticatedSession(keycloakInstance, resolvedTenant, subdomain)) return;
        } else {
          // Not authenticated (e.g. we just landed back here after logging out on a company
          // subdomain). `keycloak_session_active` is sessionStorage — per-origin — so a logout on a
          // different subdomain could NOT have cleared this origin's copy. Left stale, the NEXT
          // login would be treated as an existing session and skip company selection. Clear it here
          // so the next authentication runs the new-login path (password setup + picker).
          sessionStorage.removeItem('keycloak_session_active');
        }
        setKeycloak(keycloakInstance);
        setInitialized(true);
      } catch (error) {
        console.error('Failed to initialize Keycloak:', error);
        setInitialized(true);
      }
    };
    initializeKeycloak();
  }, [setAuth, storeLogout, setProfile, clearProfile]);


  // Proactive token refresh, checked every 60 seconds:
  //  - active since the last refresh and that was over a minute ago → forced refresh, which extends
  //    the Keycloak session in step with the user's activity;
  //  - otherwise → refresh only when the token expires within 60 seconds.
  useEffect(() => {
    if (!keycloak?.authenticated) return;
    const tokenCheckInterval = setInterval(() => {
      const activeSinceRefresh = getLastActivity() > lastRefreshAtRef.current;
      const refreshDue = Date.now() - lastRefreshAtRef.current >= ACTIVE_REFRESH_INTERVAL_MS;
      return runProactiveRefresh(keycloak, activeSinceRefresh && refreshDue ? -1 : 60);
    }, 60000);
    return () => {
      clearInterval(tokenCheckInterval);
    };
  }, [keycloak?.authenticated]);

  // Idle auto-logout (IdleTimeoutDialog): "Stay signed in" extends the Keycloak session at once; the
  // idle timeout signs the user out to the logout page. Stable identities (they read the latest
  // render through a ref) so the Keycloak context value does not change every render.
  const idleActionsRef = useRef(null);
  useLayoutEffect(() => {
    idleActionsRef.current = {
      extendSession: () => (keycloak ? runProactiveRefresh(keycloak, -1) : undefined),
      logoutForIdle: () => logoutUser(keycloak),
    };
  });
  const extendSession = useCallback(() => idleActionsRef.current?.extendSession(), []);
  const logoutForIdle = useCallback(() => idleActionsRef.current?.logoutForIdle(), []);

  return {
    keycloak,
    initialized,
    idpHint,
    domainWarning,
    dismissDomainWarning: () => setDomainWarning(false),
    extendSession,
    logoutForIdle,
  };
}
